import React from 'react';
import { Code as CodeIcon, Users as UsersIcon } from 'lucide-react';
import { GameInstance, GameState } from '../../../../../types/types';
import { Card, CardContent, CardFooter } from '../../../../ui/card';
import { Button } from '../../../../ui/button';
import { Badge } from '../../../../ui/badge';

/**
 * Component to display a game card with details about a specific game instance.
 * @param game The game instance to display.
 * @param handleJoin Function to handle joining the game. Takes the game ID as an argument.
 * @returns A React component rendering the game details and a join button if the game is waiting to start.
 */
const GameCard = ({
  game,
  handleJoin,
}: {
  game: GameInstance<GameState>;
  handleJoin: (gameID: string) => void;
}) => {
  // Helper function to determine badge variant based on game status
  const getStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'outline' => {
    if (status === 'IN_PROGRESS') return 'default';
    if (status === 'OVER') return 'secondary';
    return 'outline';
  };

  const isCodeQuiz = game.gameType === 'CodeQuiz';

  // Filter out duplicate players
  const uniquePlayers = [...new Set(game.players)];

  return (
    <Card
      className={`shadow-sm hover:shadow-md transition-shadow ${isCodeQuiz ? 'border-indigo-200' : ''}`}>
      <CardContent className='pt-6'>
        <div className='flex justify-between items-start mb-4'>
          <div>
            <p className='mb-1'>
              <span className='font-semibold'>Game ID:</span> {game.gameID}
            </p>
            <Badge variant={getStatusBadgeVariant(game.state.status)}>{game.state.status}</Badge>
          </div>
          <Badge
            variant={isCodeQuiz ? 'default' : 'outline'}
            className={isCodeQuiz ? 'bg-indigo-500' : ''}>
            {isCodeQuiz ? (
              <span className='flex items-center'>
                <CodeIcon className='w-3 h-3 mr-1' /> Code Quiz
              </span>
            ) : (
              game.gameType
            )}
          </Badge>
        </div>
        <div className='mt-3'>
          <div className='flex items-center mb-2 text-sm text-gray-600'>
            <UsersIcon className='w-4 h-4 mr-1' /> Players
          </div>
          <ul className='space-y-1'>
            {uniquePlayers.map((player: string, index: number) => (
              <li
                key={`${game.gameID}-${player}-${index}`}
                className='bg-gray-100 rounded py-1 px-2 text-sm'>
                {player}
              </li>
            ))}
            {game.players.length === 0 && (
              <li className='text-gray-500 text-sm italic'>No players yet</li>
            )}
          </ul>
        </div>
      </CardContent>
      {game.state.status === 'WAITING_TO_START' && (
        <CardFooter className='pt-2 pb-4'>
          <Button
            variant='default'
            onClick={() => handleJoin(game.gameID)}
            className={`w-full ${isCodeQuiz ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}>
            Join Game
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default GameCard;
